import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { Reveal } from "../components/motion/Reveal";
import { SunIcon } from "../components/brand/icons";

const TYPES = [
  { value: "press", label: "Press" },
  { value: "art_sales", label: "Art Sales" },
  { value: "partnerships", label: "Partnerships" },
  { value: "other", label: "Other" },
];

const inputClass =
  "w-full border border-charcoal bg-transparent px-3 py-2.5 text-sm font-light outline-none focus:ring-1 focus:ring-charcoal";

export default function ContactPage() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", type: "press", message: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useSeo({
    title: "Enquiries | Pantheon Art",
    description: "Contact Pantheon Art for press, art sales and partnership enquiries.",
    path: "/contact",
  });

  useEffect(() => {
    api.get("/settings/site").then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/enquiries", form);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 pb-24 pt-32 lg:px-10 lg:pt-40" data-testid="contact-page">
      <Reveal>
        <h1 className="h-condensed text-[clamp(3rem,8vw,7rem)]">Enquiries</h1>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="h-long mt-6 max-w-[52ch] text-lg lg:text-xl">
          For press, art sales and partnership enquiries, please use the form below.
        </p>
      </Reveal>

      <div className="hairline-t mt-12 grid gap-14 pt-12 lg:grid-cols-2">
        <div>
          {sent ? (
            <div className="flex h-full min-h-[320px] flex-col items-start justify-center" data-testid="contact-success">
              <SunIcon className="h-10 w-10" />
              <p className="h-condensed mt-6 text-2xl">Thank you</p>
              <p className="body-text mt-3 text-base">
                Your enquiry has been sent. We will reply to you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="eyebrow mb-2 block text-[0.65rem]">Name</label>
                <input id="contact-name" data-testid="contact-name-input" type="text" required autoComplete="name" value={form.name} onChange={set("name")} className={inputClass} />
              </div>
              <div>
                <label htmlFor="contact-email" className="eyebrow mb-2 block text-[0.65rem]">Email</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required autoComplete="email" value={form.email} onChange={set("email")} className={inputClass} />
              </div>
              <div>
                <label htmlFor="contact-type" className="eyebrow mb-2 block text-[0.65rem]">Enquiry type</label>
                <select id="contact-type" data-testid="contact-type-select" value={form.type} onChange={set("type")} className={inputClass}>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="eyebrow mb-2 block text-[0.65rem]">Message</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} className={inputClass} />
              </div>
              <div className="absolute -left-[9999px] top-0" aria-hidden="true">
                <label htmlFor="contact-company">Company</label>
                <input id="contact-company" data-testid="contact-honeypot" type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
              </div>
              {error && (
                <p className="text-sm" role="alert" data-testid="contact-error">{error}</p>
              )}
              <button type="submit" data-testid="contact-submit-button" disabled={busy} className="link-arrow text-xs disabled:opacity-50">
                {busy ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          )}
        </div>
        <div className="space-y-10 lg:border-l lg:pl-14" style={{ borderColor: "var(--charcoal)" }}>
          <div>
            <p className="eyebrow text-[0.65rem] opacity-60">Email</p>
            <a href="mailto:enquiries@pantheonart.com" data-testid="contact-email-link" className="link-arrow mt-3 text-sm">
              enquiries@pantheonart.com
            </a>
          </div>
          <div>
            <p className="eyebrow text-[0.65rem] opacity-60">London</p>
            <p className="font-serifbrand mt-3 text-lg leading-snug" data-testid="contact-address">
              {settings?.footer_address || "48 Curzon Street, London W1J 7UL, UK"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
